<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';

import {
	SingleSelectList,
	SingleSelectListItem,
	FormControl,
	FormLabel,
	FormMessage,
	FormItem,
	FormField,
} from '@laam/ui/base';

import type { ProductOption, ProductVariant } from '~/types/Product';

import { type CustomFormTypes } from './size-chart/types';
import CustomSizeForm from './size-chart/CustomSizeForm.vue';
import SizeChart from './size-chart/SizeChart.vue';
import ProductVariantSize from '~/components/product-detail/ProductVariantSize.vue';
import SizeChartModal from '~/components/product-detail/size-chart/SizeChartModal.vue';
import { isMobileView } from '~/utils/helpers.ts';
import type { ProductSchema } from '@laam/cms-shared';

const { $eventClient } = useNuxtApp();

interface ProductFormProps {
	productData: ProductSchema;
	options: ProductOption[];
	variants?: ProductVariant[];
	sizeChartId: number;
	quantity: number;
	fromTest: boolean;
}
const props = defineProps<ProductFormProps>();
const emit = defineEmits([
	'selectedVariantData',
	'onSuccess',
	'onInvalidSubmit',
	'saveCustomform',
]);

const customForm = ref<CustomFormTypes>();

const showCustomFormError = ref(false);
const productFormSchema = toTypedSchema(
	z.object(
		Object.fromEntries(
			props.options.map((option) => {
				const optionName = option.name.toLowerCase();
				return [
					optionName,
					z
						.string({ required_error: `You need to select ${option.name}.` })
						.refine((value) => option.values.includes(value)),
				];
			}),
		),
	),
);

const form = useForm({
	validationSchema: productFormSchema,
});

const onSubmit = form.handleSubmit(onSuccess, onInvalidSubmit);

const selectedVariant = ref<ProductVariant | null>();

const handleSizeSelect = (size: string, option: ProductOption) => {
	if (option) {
		$eventClient.sendEvent('selected-variant', {
			variantName: option.name,
			variantValue: size,
		});
	}
};

async function onSuccess() {
	emit('onSuccess');
}

function onInvalidSubmit() {
	emit('onInvalidSubmit');
}

onMounted(() => {
	if (props.options.length === 1 && props.options[0]!.values.length === 1) {
		form.setFieldValue(
			props.options[0]!.name.toLowerCase(),
			props.options[0]!.values[0]!,
		);
	}
});

watch(form.values, checkVariant);
async function checkVariant() {
	const optionsArray = Object.values(form.values);

	const sortedSelectedTitle = optionsArray.sort().join(' / ');

	// Check if any variant matches the sorted and joined title
	props.variants!.some((variant) => {
		const sortedVariantTitle = variant.title.split(' / ').sort().join(' / ');
		const exists = sortedVariantTitle === sortedSelectedTitle;

		if (exists) {
			selectedVariant.value = variant;
			emit('selectedVariantData', selectedVariant.value);
		} else selectedVariant.value = null;
	});
}

const showSizeChartButton = computed(() => {
	return !!props.sizeChartId;
});
const saveCustomform = (value: CustomFormTypes) => {
	customForm.value = value;
	emit('saveCustomform', customForm.value);
};
const isQuickAddPDP = inject('quickAddForm', ref(false));
</script>

<template>
	<form
		:id="isQuickAddPDP ? 'quickAddForm' : 'productForm'"
		class="pdp__form"
		@submit="onSubmit"
	>
		<div
			v-for="(option, index) in props.options"
			:key="index"
			data-testid="product-variant-group"
		>
			<FormField
				:key="option.name.toLowerCase() + index"
				v-slot="{ componentField }"
				:class="'product_form' + option.name.toLowerCase()"
				:name="option.name.toLowerCase()"
				type="radio"
			>
				<FormItem
					class="pdp--form-item gap-3xl lg:gap-md px-3xl lg:p-0 flex flex-col"
				>
					<div class="gap-3xl flex items-center w-full">
						<FormLabel
							class="text-md font-semibold text-gray-800"
							:class="'pdp__form-options-label-' + option.name.toLowerCase()"
							:for="option.name.toLowerCase()"
						>
							{{ option.name }}
						</FormLabel>
						<div
							v-if="
								(option.name.toLowerCase().includes('size') ||
									option.name.toLowerCase().includes('accessories')) &&
								showSizeChartButton
							"
							class="pdp__size-chart"
							@click="
								() => {
									$eventClient.sendEvent('view-size-chart', {
										l4Node: props.productData.node_l4,
										l3Node: props.productData.node_l3,
										noOfpieces:
											props.productData.attributes['number-of-pieces'],
									});
								}
							"
						>
							<SizeChart v-if="isMobileView()" />
							<SizeChartModal v-else />
						</div>

						<FormMessage />
						<p
							v-if="showCustomFormError"
							class="text-error-500 text-sm font-medium"
						>
							Please fill in size form
						</p>
					</div>

					<FormControl class="">
						<SingleSelectList
							v-bind="componentField"
							class="gap-md flex flex-row flex-wrap"
						>
							<SingleSelectListItem
								v-for="size in option.values"
								:key="size"
								class="pdp__form-options-button"
								:value="size"
								data-testid="variant-group-option"
								@click="handleSizeSelect(size, option)"
							>
								<ClientOnly v-if="size === 'Custom Size'">
									<CustomSizeForm
										:option="option"
										@save-data="saveCustomform"
									/>
									<template #fallback>
										<!-- this will be rendered on server side -->
										Custom Size
									</template>
								</ClientOnly>

								<ProductVariantSize
									v-else
									class="pdp_product-variant-size"
									:option="option.name"
									:title="size"
									:from-test="props.fromTest"
								/>
							</SingleSelectListItem>
						</SingleSelectList>
					</FormControl>
				</FormItem>
			</FormField>
		</div>
	</form>
</template>

<style scoped>
.pdp__form-options-button {
	@apply p-md w-fit rounded-xs border border-solid border-gray-300 text-gray-700 bg-white data-[state=checked]:text-white data-[state=checked]:border-gray-700 data-[state=checked]:bg-gray-700 text-sm font-medium min-w-10xl;
}
</style>
